import React, { useCallback, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { observer } from 'mobx-react'
import { Button, Descriptions, Divider, Skeleton } from 'antd'
import { EyeOutlined } from '@ant-design/icons'
import { BottomSheet } from '@pushly/aqe/lib/components/bottom-sheet/bottom-sheet'
import { StatusBadge, SourceBadge } from '@pushly/aqe/lib/components/badges'
import { useAppState } from '../hooks/use-mobile-service'
import { useNotificationDetail } from '../hooks/use-notification-detail'
import { useNotificationActions } from '../hooks/use-notification-actions'
import { useNotificationPermissions } from '../hooks/use-notification-permissions'
import { StatusType } from '../../enums/status-type'
import { NotificationSource } from '@pushly/models/lib/enums/notification-source'
import { isNotificationEditable } from '../../features/notifications/helpers/notification-helpers'
import { getDeliveryStrategyLabel } from '../notifications/shared/notification-constants'
import { NotificationActions } from '../notifications/shared/notification-actions'
import { ChannelList } from '../components/channel-list'
import { AudienceDisplay } from '../components/audience-display'
import { formatNotificationDate } from '../utils/format-notification-date'
import { mobileRoutes } from '../utils/mobile-routes'
import { getNotificationAttribution } from '../../_utils/notification-attribution'
import { NotificationAttributionText } from '../../components/notification-attribution/notification-attribution-text'
import './notification-quick-sheet.scss'

interface NotificationQuickSheetProps {
    /** Notification ID to fetch and display */
    notificationId: string | number | null
    /** Whether the sheet is open */
    open: boolean
    /** Called when the sheet should close */
    onClose: () => void
    /** Called after a mutation (cancel/archive) so the parent can refresh */
    onChange?: () => void
    /** Controls which actions are shown. Default: 'notification' */
    variant?: 'notification' | 'draft'
    /** Called when a draft is archived (variant='draft' only) */
    onArchive?: (notificationId: number) => void | Promise<void>
}

/**
 * Standalone notification quick-view bottom sheet.
 * Fetches full notification details by ID on open.
 * Reusable across notification list cards and insights top notifications.
 */
export const NotificationQuickSheet: React.FC<NotificationQuickSheetProps> = observer(
    ({ notificationId, open, onClose, onChange, variant = 'notification', onArchive }) => {
        const appState = useAppState()
        const history = useHistory()

        const domainId = appState.currentDomain?.id
        const timezone = appState.currentDomain?.timezone ?? 'UTC'

        const { data: notif, loading } = useNotificationDetail(domainId, notificationId, {
            enabled: open,
            cancellationKey: 'mv.quick-sheet.fetch',
        })
        // Derived values
        const title = notif?.template?.channels?.default?.title ?? 'Untitled'
        const body = notif?.template?.channels?.default?.body
        const imageUrl = notif?.template?.channels?.default?.image_url
        const channels: string[] = notif?.channels ?? []
        const audience = notif?.audience
        const targetedSegments = audience?.segments ?? []
        const excludedSegments = audience?.excluded_segments ?? []
        const attribution = getNotificationAttribution({
            source: notif?.source,
            createdByName: notif?.created_by_user_name,
            feed: notif?.feed,
        })

        const formattedTime = useMemo(
            () => formatNotificationDate(notif?.delivery_spec?.send_date_utc, timezone),
            [notif, timezone],
        )

        const deliveryStrategy = useMemo(() => (notif ? getDeliveryStrategyLabel(notif) : undefined), [notif])

        // Permissions
        const { canCreate, canUpdate } = useNotificationPermissions()
        const isScheduled = notif?.status === StatusType.SCHEDULED.name
        const editable = notif && isNotificationEditable(notif)

        const { handleEdit, handleDuplicate, handleCancel, handleArchive } = useNotificationActions({
            domainId,
            notificationId,
            title,
            scheduleId: notif?.schedules?.[0]?.id ?? notif?.id,
            onBeforeNavigate: onClose,
            onChange,
        })

        const handleViewFull = useCallback(() => {
            onClose()
            if (domainId && notificationId) {
                history.push(mobileRoutes.notificationSummary(domainId, notificationId))
            }
        }, [onClose, domainId, notificationId, history])

        const isDraftVariant = variant === 'draft'

        const handleArchiveDraft = useCallback(() => {
            if (!onArchive) return
            handleArchive((id) => {
                onArchive(id)
                onChange?.()
            })
        }, [handleArchive, onArchive, onChange])

        return (
            <BottomSheet
                open={open}
                onClose={onClose}
                title={isDraftVariant ? 'Draft Quick View' : 'Notification Quick View'}
                height="auto"
            >
                <div className="notification-quick-sheet">
                    {loading && <Skeleton active paragraph={{ rows: 5 }} />}

                    {!loading && notif && (
                        <>
                            {/* Title + Body + Image */}
                            <div className="notification-quick-sheet__title">{title}</div>
                            {body && <div className="notification-quick-sheet__body">{body}</div>}
                            {imageUrl && (
                                <img className="notification-quick-sheet__image" src={imageUrl} alt="" loading="lazy" />
                            )}

                            <Divider className="notification-quick-sheet__divider" />

                            {/* Feed attribution — self-contained text; the quick sheet did not
                                previously show creators, so only feed-sourced rows are attributed. */}
                            {attribution.isFeed && (
                                <NotificationAttributionText
                                    as="div"
                                    className="notification-quick-sheet__attribution"
                                    attribution={attribution}
                                />
                            )}

                            {/* Metadata */}
                            <Descriptions
                                column={1}
                                size="small"
                                colon={false}
                                labelStyle={{ width: 90, color: 'var(--color-gray-dark)' }}
                            >
                                <Descriptions.Item label="Status">
                                    <StatusBadge type="notification" status={notif.status ?? ''} expanded />
                                </Descriptions.Item>
                                {notif.source && notif.source !== NotificationSource.MANUAL && (
                                    <Descriptions.Item label="Source">
                                        <SourceBadge type="notification" source={notif.source} expanded />
                                    </Descriptions.Item>
                                )}
                                <Descriptions.Item label="Send Date">{formattedTime}</Descriptions.Item>
                                {channels.length > 0 && (
                                    <Descriptions.Item label="Channels">
                                        <ChannelList channels={channels} />
                                    </Descriptions.Item>
                                )}
                                {(targetedSegments.length > 0 || audience?.all_subscribers) && (
                                    <Descriptions.Item label="Audience">
                                        <AudienceDisplay
                                            allSubscribers={audience?.all_subscribers}
                                            segments={targetedSegments}
                                        />
                                    </Descriptions.Item>
                                )}
                                {deliveryStrategy && (
                                    <Descriptions.Item label="Strategy">{deliveryStrategy}</Descriptions.Item>
                                )}
                            </Descriptions>

                            <Divider className="notification-quick-sheet__divider" />

                            {/* View Full Details — primary action, top of stack (not shown for drafts) */}
                            {!isDraftVariant && (
                                <Button type="primary" icon={<EyeOutlined />} onClick={handleViewFull} block>
                                    View Full Details
                                </Button>
                            )}

                            {/* Actions */}
                            <NotificationActions
                                className="notification-quick-sheet__actions"
                                canEdit={isDraftVariant ? canUpdate : !!(editable && isScheduled && canUpdate)}
                                canDuplicate={canCreate}
                                canCancel={!isDraftVariant && !!(isScheduled && canUpdate)}
                                canArchive={isDraftVariant && canUpdate}
                                onEdit={handleEdit}
                                onDuplicate={handleDuplicate}
                                onCancel={handleCancel}
                                onArchive={handleArchiveDraft}
                            />
                        </>
                    )}
                </div>
            </BottomSheet>
        )
    },
)

NotificationQuickSheet.displayName = 'NotificationQuickSheet'
